import { useReducer } from "react";

import { IStoreInvoiceFilters } from "src/@types/store-invoice";

import { DEFAULT_PER_PAGE, FIRST_PAGE } from "../types";

export type StoreInvoiceOptionalField =
  | "invoiceStatus"
  | "saleStatus"
  | "documentType";

export interface StoreInvoiceFilterDraft {
  readonly storeCode: number | undefined;
  readonly startDate: string | undefined;
  readonly endDate: string | undefined;
  readonly invoiceStatus: string | undefined;
  readonly saleStatus: string | undefined;
  readonly documentType: string | undefined;
  readonly series: string;
}

export interface StoreInvoiceFilterState {
  readonly draft: StoreInvoiceFilterDraft;
  readonly applied: IStoreInvoiceFilters | undefined;
  readonly page: number;
  readonly perPage: number;
}

export type StoreInvoiceFilterAction =
  | { type: "SET_STORE"; payload: number | undefined }
  | {
      type: "SET_PERIOD";
      payload: {
        startDate: string | undefined;
        endDate: string | undefined;
      };
    }
  | {
      type: "SET_OPTIONAL_FIELD";
      payload: {
        field: StoreInvoiceOptionalField;
        value: string | undefined;
      };
    }
  | { type: "SET_SERIES"; payload: string }
  | { type: "APPLY_FILTERS"; payload: IStoreInvoiceFilters }
  | { type: "SET_PAGE"; payload: number }
  | { type: "SET_PAGE_SIZE"; payload: number };

const INITIAL_DRAFT: StoreInvoiceFilterDraft = {
  storeCode: undefined,
  startDate: undefined,
  endDate: undefined,
  invoiceStatus: undefined,
  saleStatus: undefined,
  documentType: undefined,
  series: "",
};

export const INITIAL_STORE_INVOICE_FILTER_STATE: StoreInvoiceFilterState = {
  draft: INITIAL_DRAFT,
  applied: undefined,
  page: FIRST_PAGE,
  perPage: DEFAULT_PER_PAGE,
};

export function storeInvoiceFilterReducer(
  state: StoreInvoiceFilterState,
  action: StoreInvoiceFilterAction,
): StoreInvoiceFilterState {
  if (action.type === "SET_STORE") {
    return { ...state, draft: { ...state.draft, storeCode: action.payload } };
  }

  if (action.type === "SET_PERIOD") {
    return {
      ...state,
      draft: {
        ...state.draft,
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      },
    };
  }

  if (action.type === "SET_OPTIONAL_FIELD") {
    return {
      ...state,
      draft: { ...state.draft, [action.payload.field]: action.payload.value },
    };
  }

  if (action.type === "SET_SERIES") {
    return { ...state, draft: { ...state.draft, series: action.payload } };
  }

  if (action.type === "APPLY_FILTERS") {
    return { ...state, applied: action.payload, page: FIRST_PAGE };
  }

  if (action.type === "SET_PAGE") {
    return { ...state, page: action.payload };
  }

  if (action.type === "SET_PAGE_SIZE") {
    return { ...state, perPage: action.payload, page: FIRST_PAGE };
  }

  return state;
}

export interface StoreInvoiceFilterMethods {
  readonly setStore: (storeCode: number | undefined) => void;
  readonly setPeriod: (
    startDate: string | undefined,
    endDate: string | undefined,
  ) => void;
  readonly setOptionalField: (
    field: StoreInvoiceOptionalField,
    value: string | undefined,
  ) => void;
  readonly setSeries: (value: string) => void;
  readonly applyFilters: (filters: IStoreInvoiceFilters) => void;
  readonly setPage: (page: number) => void;
  readonly setPageSize: (perPage: number) => void;
}

export interface UseStoreInvoiceFilterReducer {
  readonly state: StoreInvoiceFilterState;
  readonly methods: StoreInvoiceFilterMethods;
}

export function useStoreInvoiceFilterReducer(): UseStoreInvoiceFilterReducer {
  const [state, dispatch] = useReducer(
    storeInvoiceFilterReducer,
    INITIAL_STORE_INVOICE_FILTER_STATE,
  );

  const methods: StoreInvoiceFilterMethods = {
    setStore: (storeCode: number | undefined): void =>
      dispatch({ type: "SET_STORE", payload: storeCode }),

    setPeriod: (
      startDate: string | undefined,
      endDate: string | undefined,
    ): void => dispatch({ type: "SET_PERIOD", payload: { startDate, endDate } }),

    setOptionalField: (
      field: StoreInvoiceOptionalField,
      value: string | undefined,
    ): void =>
      dispatch({ type: "SET_OPTIONAL_FIELD", payload: { field, value } }),

    setSeries: (value: string): void =>
      dispatch({ type: "SET_SERIES", payload: value }),

    applyFilters: (filters: IStoreInvoiceFilters): void =>
      dispatch({ type: "APPLY_FILTERS", payload: filters }),

    setPage: (page: number): void =>
      dispatch({ type: "SET_PAGE", payload: page }),

    setPageSize: (perPage: number): void =>
      dispatch({ type: "SET_PAGE_SIZE", payload: perPage }),
  };

  return { state, methods };
}
